import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { RunnerLayout } from '../components/RunnerLayout';
import { Controls } from '../components/Controls';
import { ConfirmModal } from '../components/ConfirmModal';
import { useCodePersistence, loadSavedCode } from '../hooks/useCodePersistence';
import { useCodeRunner } from '../hooks/useCodeRunner';
import { useWorkspace } from '../hooks/useWorkspace';
import { DEFAULT_LANGUAGE } from '../config/languages';
import type { Problem } from '../types/problem';
import type { TestResult } from '../types/worker';
import type { LanguageId } from '../types/language';
import { useMutation, useQuery, useConvexAuth } from 'convex/react';
import { useUser } from '@clerk/clerk-react';
import { api } from '../../../../backend/convex/_generated/api';
import { NotFound } from './NotFound';
import '../App.css';

export const ProblemEditor = () => {
  const { problemId } = useParams<{ problemId: string }>();
  const navigate = useNavigate();
  const { isLoading: isAuthLoading } = useConvexAuth();
  const [language] = useState<LanguageId>(DEFAULT_LANGUAGE);
  const { status, runCode, runTests } = useCodeRunner(language);
  const problem = useQuery(api.problems.get, 
    (problemId && !isAuthLoading) ? { id: problemId } : "skip"
  );
  
  const [currentProblem, setCurrentProblem] = useState<Problem | null>(null);
  const [currentCode, setCurrentCode] = useState<string>('');
  const [showResetModal, setShowResetModal] = useState(false);
  const [showSubmitModal, setShowSubmitModal] = useState(false);
  
  const createSubmission = useMutation(api.submissions.createSubmission);
  const getOrCreateWorkspace = useMutation(api.workspaces.getOrCreateDefaultWorkspace);
  const { user } = useUser();
  
  // Get workspace to check feature flags
  // If problem has a workspaceId, use that; otherwise get default workspace
  const defaultWorkspace = useQuery(api.workspaces.getDefaultWorkspace, !isAuthLoading ? {} : "skip");
  const workspaceFromProblem = useWorkspace(problem?.workspaceId);
  const workspace = problem?.workspaceId ? workspaceFromProblem.workspace : (defaultWorkspace ? { ...defaultWorkspace, role: null } : null);
  const submissionsEnabled = workspace?.features?.submissions !== false;
  
  // Mobile state
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Results state
  const [resultsMode, setResultsMode] = useState<'idle' | 'code' | 'tests'>('idle');
  const [testResults, setTestResults] = useState<TestResult[]>([]);
  const [outputStdout, setOutputStdout] = useState('');
  const [outputStderr, setOutputStderr] = useState('');
  const [outputError, setOutputError] = useState('');

  useEffect(() => {
    if (problem) {
      setCurrentProblem(problem as Problem);
      const code = loadSavedCode(problem.id, problem.starterCode);
      setCurrentCode(code);
    }
  }, [problem]);

  if (problem === null) {
    return <NotFound />;
  }

  useEffect(() => {
    if (currentProblem) {
      document.title = `LibreRank - ${currentProblem.title}`;
    }
  }, [currentProblem]);

  // Auto-save code to localStorage
  useCodePersistence(currentProblem?.id || null, currentCode);

  const handleResetCode = () => {
    setShowResetModal(true);
  };

  const confirmResetCode = () => {
    if (currentProblem) {
      setCurrentCode(currentProblem.starterCode);
    }
    setShowResetModal(false);
  };

  const cancelResetCode = () => {
    setShowResetModal(false);
  };

  const handleRun = async () => {
    if (!currentProblem) {
      // No problem selected - run code without tests
      const result = await runCode(currentCode);
      setResultsMode('code');
      setOutputStdout(result.stdout);
      setOutputStderr(result.stderr);
      setOutputError(result.error || '');
      setTestResults([]);
      return;
    }

    // Problem selected - run tests
    const result = await runTests(currentCode, currentProblem.functionName, currentProblem.publicTests);
    setResultsMode('tests');
    setTestResults(result.results);
    setOutputStdout(result.stdout);
    setOutputStderr(result.stderr);
    setOutputError(result.error || '');
  };

  const handleSubmit = async () => {
    setShowSubmitModal(true);
  };

  const confirmSubmit = async () => {
    if (!currentProblem || !user) return;

    try {
      const workspaceId = await getOrCreateWorkspace();
      await createSubmission({
        workspaceId,
        problemId: currentProblem.id,
        language: language,
        source: currentCode,
      });
      console.log('Submission successful');
      navigate('/');
    } catch (error) {
      console.error('Failed to record submission:', error);
    } finally {
      setShowSubmitModal(false);
    }
  };

  const cancelSubmit = () => {
    setShowSubmitModal(false);
  };

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      const isMod = /Mac/.test(navigator.userAgent) ? e.metaKey : e.ctrlKey;
      
      if (isMod && e.key === 'Enter') {
        e.preventDefault();
        handleSubmit();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [currentCode, user, currentProblem]); // dependencies for handleSubmit context

  const controls = (
    <Controls
      status={status}
      isLoggedIn={!!user}
      onRun={handleRun}
      onSubmit={handleSubmit}
      onResetCode={handleResetCode}
      showSubmit={submissionsEnabled}
    />
  );

  return (
    <>
      <ConfirmModal
        isOpen={showResetModal}
        title="Reset Code"
        message="Are you sure you want to reset your code? All your changes will be lost."
        confirmText="Reset Code"
        confirmVariant="destructive"
        onConfirm={confirmResetCode}
        onCancel={cancelResetCode}
      />
      <ConfirmModal
        isOpen={showSubmitModal}
        title="Submit Solution"
        message="Are you sure you want to submit your solution? This will be recorded as your final answer."
        confirmText="Submit Code"
        confirmVariant="success"
        onConfirm={confirmSubmit}
        onCancel={cancelSubmit}
      />
      
      <RunnerLayout
        problem={currentProblem}
        code={currentCode}
        onCodeChange={setCurrentCode}
        language={language}
        onRun={handleRun}
        resultsMode={resultsMode}
        testResults={testResults}
        stdout={outputStdout}
        stderr={outputStderr}
        error={outputError}
        controls={controls}
        isMobile={isMobile}
      />
    </>
  );
};

